import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { AnimateIn, AnimateInProps } from '../animate-in';
import { BackToTop } from '../back-to-top';
import { BannerCarousel } from '../banner-carousel';

const useStyles = makeStyles({
  content: {
    width: '94%',
    margin: '0 auto',
    paddingTop: 10,
  },
  wrapper: {
    flex: 1,
    overflowX: 'auto',
  },
});

/**
 * The scrolling page body: the banner carousel (not on admin pages), the
 * page content in a 94% column, then the full-width footer slot.
 *
 * @param footer - Rendered after the content column, edge to edge (the ad
 *   carousel and the Alliance footer)
 * @param paddingBottom - Space under the content (default 100, or 48 above
 *   a footer)
 */
export function RouteWrapper(props: {
  children: React.ReactNode;
  footer?: React.ReactNode;
  paddingBottom?: number;
  nofade?: AnimateInProps['nofade'];
  notransform?: AnimateInProps['notransform'];
}) {
  // State
  const [showButtonTop, setShowButtonTop] = React.useState(false);
  // Hooks
  const wrapperEl = React.useRef<HTMLDivElement>(null);
  const classes = useStyles();

  // Hide banner carousel for admin pages
  const location = useLocation();
  const isAdminPath = location.pathname.includes('/admin/');

  React.useEffect(() => {
    const wrapperDiv = wrapperEl.current;

    wrapperDiv?.addEventListener('scroll', (event) => {
      const target = event.target as HTMLTextAreaElement;
      if (target.scrollTop > 500) {
        setShowButtonTop(true);
      } else {
        setShowButtonTop(false);
      }
    });

    return () => wrapperDiv?.removeEventListener('scroll', () => {});
  }, [wrapperEl]);

  const handleScrollToTop = () => {
    if (wrapperEl.current !== null) {
      wrapperEl.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const paddingBottom =
    props.paddingBottom !== undefined
      ? props.paddingBottom
      : props.footer
        ? 48
        : 100;

  return (
    <React.Fragment>
      <AnimateIn
        to="top"
        ref={wrapperEl}
        className={classes.wrapper}
        nofade={props.nofade}
        notransform={props.notransform}
      >
        {!isAdminPath && <BannerCarousel />}

        <div className={classes.content} style={{ paddingBottom }}>
          {props.children}
        </div>

        {props.footer}
      </AnimateIn>

      {showButtonTop && <BackToTop onClick={handleScrollToTop} />}
    </React.Fragment>
  );
}
